import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Chip } from 'primereact/chip';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Badge } from 'primereact/badge';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import './TaskRiskAssessmentView.css';

// New API structure interfaces
export interface ControlMeasure {
  id: number;
  name: string;
}

export interface PPEItem {
  id: number;
  name: string;
  description: string;
  expected_life_value: number;
  expected_life_interval: string;
  expected_life_window: string | null;
  s3_key: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  price: string;
  size: string;
  frequency: string;
  appointment: string | null;
  required: boolean;
  photo: string;
}

export interface Risk {
  id: number;
  risk_id: string;
  name: string;
  description: string;
  rating: number;
  rating_label: string;
  likelihood: number;
  likelihood_label: string;
  consequence: number;
  consequence_label: string;
  after_rating: number;
  after_rating_label: string;
  after_likelihood: number;
  after_likelihood_label: string;
  after_consequence: number;
  after_consequence_label: string;
  review_date: string | null;
  responsible: string;
  responsible_name: string;
  type: string | null;
  record_id: string | null;
  note: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Hazard {
  hazard_id: number;
  hazard_name: string;
  risks: Risk[] | null;
  control_measures: ControlMeasure[];
}

export interface SafeWorkMethod {
  id: number;
  name: string;
  s3_key: string | null;
  photo?: string;
}

export interface JobStep {
  job_step_id: number;
  job_step_name: string;
  job_step_description: string;
  created_at: string;
  updated_at: string;
  safe_work_method: SafeWorkMethod[];
  hazards: Hazard[];
}

// Flattened display interface
export interface RiskAssessment {
  id: number;
  risk_id: string;
  job_step_name: string;
  job_step_description: string;
  hazard_id: number;
  hazard_name: string;
  risk_name: string;
  description: string;
  control_measures: ControlMeasure[];
  likelihood_label: string;
  consequence_label: string;
  rating_label: string;
  after_likelihood_label: string;
  after_consequence_label: string;
  after_rating_label: string;
  review_date: string | null;
  responsible: string;
  responsible_name?: string;
}

export interface CategoryData {
  name: string;
  title: string;
  jobSteps: JobStep[];
  riskAssessments: RiskAssessment[];
}

// Helper function to extract job steps from category tasks
export const extractJobStepsFromCategory = (tasks: any[], categoryName: string): JobStep[] => {
  const jobSteps: JobStep[] = [];

  switch (categoryName) {
    case 'job_tasks':
      tasks.forEach((taskItem: any) => {
        if (taskItem.job_steps && Array.isArray(taskItem.job_steps)) {
          jobSteps.push(...taskItem.job_steps);
        }
      });
      break;
    case 'equipment':
    case 'vehicles':
    case 'chemicals':
      jobSteps.push(...tasks.map((js: any) => ({
        job_step_id: js.id,
        job_step_name: js.name,
        job_step_description: js.description,
        created_at: js.created_at,
        updated_at: js.updated_at,
        safe_work_method: js.safe_work_method || [],
        hazards: js.hazards || []
      })));
      break;
    default:
      return [];
  }

  console.log('Extracted Job Steps:', jobSteps);
  return jobSteps;
};

// Category configuration
export const categoryConfig = {
  job_tasks: { name: 'job_tasks', title: 'Risk Assessments by Task' },
  equipment: { name: 'equipment', title: 'Risk Assessments by Equipment' },
  vehicles: { name: 'vehicles', title: 'Risk Assessments by Vehicles' },
  chemicals: { name: 'chemicals', title: 'Risk Assessments by Chemicals' }
};

// Helper function to parse API response and extract categorized job steps
export const parseApiResponse = (data: any): { categoryData: CategoryData[], siteInfo: { name: string, assessmentDate: string, reviewDate: string } } => {
  const categoryData: CategoryData[] = [];
  let siteInfo = { name: '', assessmentDate: '', reviewDate: '' };

  if (data.message === 'OK' && data.content?.categories) {
    const categories = data.content.categories;

    console.log('categories:', categories);

    Object.entries(categoryConfig).forEach(([categoryName, config]) => {
      if (categories[categoryName]) {
        console.log(`Processing category: ${categoryName}`);
        const jobSteps = extractJobStepsFromCategory(categories[categoryName], categoryName);
        if (jobSteps.length > 0) {
          const riskAssessments = processJobStepsToAssessments(jobSteps);
          categoryData.push({
            name: config.name,
            title: config.title,
            jobSteps,
            riskAssessments
          });
        }
      }
    });
  }

  if (data.content && data.content.site) {
    const site = data.content.site;
    siteInfo.name = site.name || '';
    if (site.created_at) {
      siteInfo.assessmentDate = new Date(site.created_at).toLocaleDateString();

      const createdDate = new Date(site.created_at);
      const reviewDateCalc = new Date(createdDate);
      reviewDateCalc.setFullYear(reviewDateCalc.getFullYear() + 1);
      siteInfo.reviewDate = reviewDateCalc.toLocaleDateString();
    }
  }

  console.log('Parsed Category Data:', categoryData);
  return { categoryData, siteInfo };
};

// Helper function to process job steps into flattened risk assessments
export const processJobStepsToAssessments = (jobSteps: JobStep[]): RiskAssessment[] => {
  const flattenedAssessments: RiskAssessment[] = [];

  jobSteps.forEach((jobStep: JobStep) => {
    if (!jobStep.hazards || !Array.isArray(jobStep.hazards)) {
      return;
    }

    jobStep.hazards.forEach((hazard: Hazard) => {
      if (hazard.risks && Array.isArray(hazard.risks)) {
        hazard.risks.forEach((risk: Risk) => {
          flattenedAssessments.push({
            id: risk.id,
            risk_id: risk.risk_id,
            job_step_name: jobStep.job_step_name,
            job_step_description: jobStep.job_step_description,
            hazard_id: hazard.hazard_id,
            hazard_name: hazard.hazard_name,
            risk_name: risk.name,
            description: risk.description,
            control_measures: hazard.control_measures || [],
            likelihood_label: risk.likelihood_label,
            consequence_label: risk.consequence_label,
            rating_label: risk.rating_label,
            after_likelihood_label: risk.after_likelihood_label,
            after_consequence_label: risk.after_consequence_label,
            after_rating_label: risk.after_rating_label,
            review_date: risk.review_date,
            responsible: risk.responsible,
            responsible_name: risk.responsible_name
          });
        });
      } else {
        flattenedAssessments.push({
          id: hazard.hazard_id,
          risk_id: `HAZARD-${hazard.hazard_id}`,
          job_step_name: jobStep.job_step_name,
          job_step_description: jobStep.job_step_description,
          hazard_id: hazard.hazard_id,
          hazard_name: hazard.hazard_name,
          risk_name: 'No risks identified',
          description: 'No risk description available',
          control_measures: hazard.control_measures || [],
          likelihood_label: 'N/A',
          consequence_label: 'N/A',
          rating_label: 'N/A',
          after_likelihood_label: 'N/A',
          after_consequence_label: 'N/A',
          after_rating_label: 'N/A',
          review_date: null,
          responsible: ''
        });
      }
    });
  });

  return flattenedAssessments;
};

interface RiskAssessmentContentProps {
  session: any;
  details?: any;
  signoffSection?: React.ReactNode;
  categoryData: CategoryData[];
  siteName?: string;
  assessmentDate?: string;
  reviewDate?: string;
  loading?: boolean;
}

export default function RiskAssessmentContent({ 
  session, 
  details, 
  signoffSection,
  categoryData,
  siteName = '',
  assessmentDate = '',
  reviewDate = '',
  loading = false
}: RiskAssessmentContentProps) {
  const [viewType, setViewType] = useState<'cards' | 'table'>('cards');
  const [categories, setCategories] = useState<CategoryData[]>(categoryData);
  const [searchText, setSearchText] = useState('');
  const [filteredCategories, setFilteredCategories] = useState<CategoryData[]>(categoryData);
  const [expandedJobSteps, setExpandedJobSteps] = useState<Set<string>>(new Set());
  const [expandedHazards, setExpandedHazards] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [ppeByHazard, setPpeByHazard] = useState<Map<number, PPEItem[]>>(new Map());
  const [loadingPPE, setLoadingPPE] = useState<Set<number>>(new Set());

  // View options for SelectButton
  const viewOptions = [
    { label: 'Cards', value: 'cards' },
    { label: 'Table', value: 'table' }
  ];

  // Update categories when prop changes
  useEffect(() => {
    setCategories(categoryData);
    setFilteredCategories(categoryData);
    // Remove auto-expand: sections will start collapsed
  }, [categoryData]);

  // Prefetch PPE for all hazards
  useEffect(() => {
    if (categories.length > 0) {
      const allHazardIds = categories.flatMap(category =>
        category.jobSteps.flatMap(jobStep => 
          jobStep.hazards?.map(hazard => hazard.hazard_id) || []
        )
      );
      
      allHazardIds.forEach(hazardId => {
        if (!ppeByHazard.has(hazardId) && !loadingPPE.has(hazardId)) {
          fetchPPEForHazard(hazardId);
        }
      });
    }
  }, [categories]);

  // Helper function to expand all sections by default
  const expandAllSections = (categoryData: CategoryData[]) => {
    const allJobStepIds = new Set<string>();
    const allHazardKeys = new Set<string>();
    const allCategoryNames = new Set<string>();

    categoryData?.forEach(category => {
      allCategoryNames.add(category.name);

      category.jobSteps?.forEach(js => {
        allJobStepIds.add(`${category.name}-${js.job_step_id}`);
        js.hazards?.forEach(h => {
          allHazardKeys.add(`${category.name}-${js.job_step_id}-${h.hazard_id}`);
        });
      });
    });

    setExpandedCategories(allCategoryNames);
    setExpandedJobSteps(allJobStepIds);
    setExpandedHazards(allHazardKeys);
  };

  // Filter categories based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filteredCats = categories.map(category => {
        // Filter risk assessments for table view
        const filteredAssessments = category.riskAssessments.filter(assessment =>
          assessment.job_step_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          assessment.job_step_description?.toLowerCase().includes(searchText.toLowerCase()) ||
          assessment.hazard_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          assessment.risk_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          assessment.description?.toLowerCase().includes(searchText.toLowerCase()) ||
          assessment.risk_id?.toLowerCase().includes(searchText.toLowerCase())
        );

        // Filter job steps for cards view
        const filteredJobSteps = category.jobSteps.filter(jobStep => {
          // Check if job step name or description matches
          const jobStepMatches = 
            jobStep.job_step_name?.toLowerCase().includes(searchText.toLowerCase()) ||
            jobStep.job_step_description?.toLowerCase().includes(searchText.toLowerCase());

          // Check if any hazard or risk in this job step matches
          const hasMatchingHazardOrRisk = jobStep.hazards?.some(hazard => {
            const hazardMatches = hazard.hazard_name?.toLowerCase().includes(searchText.toLowerCase());
            const riskMatches = hazard.risks?.some(risk =>
              risk.name?.toLowerCase().includes(searchText.toLowerCase()) ||
              risk.description?.toLowerCase().includes(searchText.toLowerCase()) ||
              risk.risk_id?.toLowerCase().includes(searchText.toLowerCase())
            );
            return hazardMatches || riskMatches;
          });

          return jobStepMatches || hasMatchingHazardOrRisk;
        }).map(jobStep => {
          // For each matching job step, filter its hazards to only show matching ones
          const filteredHazards = jobStep.hazards?.filter(hazard => {
            const hazardMatches = hazard.hazard_name?.toLowerCase().includes(searchText.toLowerCase());
            const hasMatchingRisk = hazard.risks?.some(risk =>
              risk.name?.toLowerCase().includes(searchText.toLowerCase()) ||
              risk.description?.toLowerCase().includes(searchText.toLowerCase()) ||
              risk.risk_id?.toLowerCase().includes(searchText.toLowerCase())
            );
            const jobStepMatches = 
              jobStep.job_step_name?.toLowerCase().includes(searchText.toLowerCase()) ||
              jobStep.job_step_description?.toLowerCase().includes(searchText.toLowerCase());

            return hazardMatches || hasMatchingRisk || jobStepMatches;
          }).map(hazard => {
            // For each matching hazard, filter its risks to only show matching ones
            const filteredRisks = hazard.risks?.filter(risk => {
              const riskMatches = 
                risk.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                risk.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                risk.risk_id?.toLowerCase().includes(searchText.toLowerCase());
              
              const hazardMatches = hazard.hazard_name?.toLowerCase().includes(searchText.toLowerCase());
              const jobStepMatches = 
                jobStep.job_step_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                jobStep.job_step_description?.toLowerCase().includes(searchText.toLowerCase());

              return riskMatches || hazardMatches || jobStepMatches;
            });

            return {
              ...hazard,
              risks: filteredRisks || []
            };
          });

          return {
            ...jobStep,
            hazards: filteredHazards || []
          };
        });

        if (filteredAssessments.length > 0 || filteredJobSteps.length > 0) {
          return {
            ...category,
            riskAssessments: filteredAssessments,
            jobSteps: filteredJobSteps
          };
        }
        return null;
      }).filter(cat => cat !== null) as CategoryData[];

      setFilteredCategories(filteredCats);
    }
  }, [searchText, categories]);

  const getRiskChipClassName = (risk: string) => {
    const riskLower = risk?.toLowerCase() || '';
    if (riskLower.includes('critical')) return 'chip-danger';
    if (riskLower.includes('high')) return 'chip-danger';
    if (riskLower.includes('medium')) return 'chip-warning';
    if (riskLower.includes('possible')) return 'chip-warning';
    if (riskLower.includes('low')) return 'chip-success';
    return 'chip-info';
  };

  const toggleJobStep = (categoryName: string, jobStepId: number) => {
    const key = `${categoryName}-${jobStepId}`;
    const newExpanded = new Set(expandedJobSteps);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedJobSteps(newExpanded);
  };

  const toggleHazard = (hazardKey: string) => {
    const newExpanded = new Set(expandedHazards);
    if (newExpanded.has(hazardKey)) {
      newExpanded.delete(hazardKey);
    } else {
      newExpanded.add(hazardKey);
    }
    setExpandedHazards(newExpanded);
  };

  // Fetch PPE items for a specific hazard
  const fetchPPEForHazard = async (hazardId: number) => {
    setLoadingPPE(prev => new Set(prev).add(hazardId));
    
    try {
      // Mock API call - replace with actual API
      // const data = await apiGet('core', `ppe?hazardId=${hazardId}`);
      
      const data = { message: 'OK', content: [] }; // Mock empty response
      
      if (data && data.message === 'OK' && data.content) {
        setPpeByHazard(prev => new Map(prev).set(hazardId, data.content));
      } else {
        setPpeByHazard(prev => new Map(prev).set(hazardId, []));
      }
    } catch (error) {
      console.error(`Error fetching PPE for hazard ${hazardId}:`, error);
      setPpeByHazard(prev => new Map(prev).set(hazardId, []));
    } finally {
      setLoadingPPE(prev => {
        const newSet = new Set(prev);
        newSet.delete(hazardId);
        return newSet;
      });
    }
  };

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  // Format control measures array to JSX list
  const formatControlMeasures = (controlMeasures: { id: number; name: string }[] | null) => {
    if (!controlMeasures || controlMeasures.length === 0) {
      return <span style={{ fontStyle: 'italic', color: '#92949c' }}>Control measures not specified</span>;
    }
    return (
      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
        {controlMeasures.map((cm) => (
          <li key={cm.id}>{cm.name}</li>
        ))}
      </ul>
    );
  };

  const renderRiskMatrix = () => (
    <Card title="Risk Assessment Matrix" className="risk-matrix-card">
      <div className="risk-matrix">
        <div className="matrix-row matrix-header-row">
          <div className="matrix-cell header-cell corner-cell">Severity →<br /><br />↓ Likelihood</div>
          <div className="matrix-cell header-cell">Minor injury/first aid</div>
          <div className="matrix-cell header-cell">Moderate injury/medical treatment</div>
          <div className="matrix-cell header-cell">Major injury/fatality</div>
        </div>
        <div className="matrix-row">
          <div className="matrix-cell likelihood-cell">Unlikely</div>
          <div className="matrix-cell low">Low</div>
          <div className="matrix-cell low">Low</div>
          <div className="matrix-cell medium">Medium</div>
        </div>
        <div className="matrix-row">
          <div className="matrix-cell likelihood-cell">Likely</div>
          <div className="matrix-cell low">Low</div>
          <div className="matrix-cell medium">Medium</div>
          <div className="matrix-cell high">High</div>
        </div>
        <div className="matrix-row">
          <div className="matrix-cell likelihood-cell">Very Likely</div>
          <div className="matrix-cell medium">Medium</div>
          <div className="matrix-cell high">High</div>
          <div className="matrix-cell high">High</div>
        </div>
      </div>
    </Card>
  );

  const renderTableView = (categoryRiskAssessments: RiskAssessment[]) => {
    return (
      <div className="table-view">
        <div className="table-scroll-hint">
          <small>Scroll horizontally to view all columns</small>
        </div>
        <DataTable 
          value={categoryRiskAssessments} 
          scrollable 
          scrollHeight="600px"
          className="risk-table"
          stripedRows
        >
          <Column field="risk_id" header="Risk ID" style={{ minWidth: '100px' }} />
          <Column field="job_step_name" header="Job Step" style={{ minWidth: '150px' }} />
          <Column field="hazard_name" header="Hazard" style={{ minWidth: '150px' }} />
          <Column field="risk_name" header="Risk" style={{ minWidth: '200px' }} />
          <Column 
            field="control_measures" 
            header="Control Measures" 
            body={(rowData) => formatControlMeasures(rowData.control_measures)}
            style={{ minWidth: '200px' }} 
          />
          <Column 
            field="rating_label" 
            header="Initial Risk"
            body={(rowData) => (
              <Chip 
                label={rowData.rating_label} 
                className={getRiskChipClassName(rowData.rating_label)}
              />
            )}
            style={{ minWidth: '120px' }} 
          />
          <Column 
            field="after_rating_label" 
            header="Residual Risk"
            body={(rowData) => (
              <Chip 
                label={rowData.after_rating_label} 
                className={getRiskChipClassName(rowData.after_rating_label)}
              />
            )}
            style={{ minWidth: '120px' }} 
          />
          <Column field="responsible_name" header="Responsible" style={{ minWidth: '150px' }} />
          <Column field="review_date" header="Review Date" style={{ minWidth: '120px' }} />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="baseline-risk-content">
      {/* Assessment Details Card */}
      <Card 
        title="Baseline Risk Assessment"
        subTitle="Hazard Identification & Risk Assessment"
        className="assessment-details-card"
      >
        <div className="assessment-info">
          <div className="info-item">
            <strong>Site Name:</strong> {siteName || 'Loading...'}
          </div>
          <div className="info-item">
            <strong>Assessment Date:</strong> {assessmentDate || 'Loading...'}
          </div>
          <div className="info-item">
            <strong>Review Date:</strong> {reviewDate || 'Loading...'}
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search" />
            </span>
            <InputText
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search risk assessments..."
              className="w-full"
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="view-toggle">
          <SelectButton
            value={viewType}
            onChange={(e) => setViewType(e.value)}
            options={viewOptions}
          />
        </div>
      </Card>

      {/* Risk Rating Legend */}
      <Card title="Risk Rating Legend" className="legend-card">
        <div className="legend-chips">
          <Chip label="High" className="chip-danger" />
          <Chip label="Medium" className="chip-warning" />
          <Chip label="Low" className="chip-success" />
        </div>
      </Card>

      {/* Risk Assessment Matrix */}
      {renderRiskMatrix()}

      {/* Risk Assessments by Category */}
      {loading ? (
        <div className="loading-container">
          <ProgressSpinner />
          <p>Loading risk assessments...</p>
        </div>
      ) : filteredCategories.length === 0 ? (
        <Card title="Risk Assessments" className="task-assessments-card">
          <div className="no-results">
            <p>No risk assessments found.</p>
          </div>
        </Card>
      ) : (
        filteredCategories.map((category, i) => {
          const isCategoryExpanded = expandedCategories.has(category.name);

          return (
            <Card 
              key={`${category.name}-${i}`} 
              className="task-assessments-card"
              title={
                <div 
                  onClick={() => toggleCategory(category.name)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>{category.title}</span>
                  <i className={`pi ${isCategoryExpanded ? 'pi-minus' : 'pi-plus'}`} />
                </div>
              }
            >
              {isCategoryExpanded && (
                <>
                  {!Array.isArray(category.jobSteps) || category.jobSteps.length === 0 ? (
                    <div className="no-results">
                      <p>No risk assessments found for this category.</p>
                    </div>
                  ) : viewType === 'cards' ? (
                    // Cards View - Hierarchical Structure: Job Step → Hazard → Risk
                    category.jobSteps.map((jobStep) => {
                      const jobStepKey = `${category.name}-${jobStep.job_step_id}`;
                      const isJobStepExpanded = expandedJobSteps.has(jobStepKey);
                      const totalRisks = jobStep.hazards?.reduce((sum, h) => sum + (h.risks?.length || 0), 0) || 0;

                      return (
                        <Card 
                          key={jobStepKey} 
                          className="job-step-card"
                          title={
                            <div
                              onClick={() => toggleJobStep(category.name, jobStep.job_step_id)}
                              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                              <div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '4px' }}>
                                  {jobStep.job_step_name}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'normal' }}>
                                  {jobStep.job_step_description}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Badge 
                                  value={`${totalRisks} risk${totalRisks !== 1 ? 's' : ''}`} 
                                />
                                <i className={`pi ${isJobStepExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                              </div>
                            </div>
                          }
                        >
                          {isJobStepExpanded && (
                            <div style={{ padding: '0 6px 4px 6px' }}>
                              {jobStep.hazards && jobStep.hazards.length > 0 ? (
                                jobStep.hazards.map((hazard) => {
                                  const hazardKey = `${category.name}-${jobStep.job_step_id}-${hazard.hazard_id}`;
                                  const isHazardExpanded = expandedHazards.has(hazardKey);
                                  const hazardPPE = ppeByHazard.get(hazard.hazard_id) || [];
                                  const isPPELoading = loadingPPE.has(hazard.hazard_id);

                                  return (
                                    <Card 
                                      key={hazardKey} 
                                      className="hazard-card"
                                      title={
                                        <div
                                          onClick={() => toggleHazard(hazardKey)}
                                          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        >
                                          <div>
                                            <strong>Hazard:</strong> {hazard.hazard_name}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Badge 
                                              value={`${hazard.risks?.length || 0} risk${(hazard.risks?.length || 0) !== 1 ? 's' : ''}`} 
                                            />
                                            <i className={`pi ${isHazardExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                                          </div>
                                        </div>
                                      }
                                    >
                                      {isHazardExpanded && (
                                        <div>
                                          {/* Control Measures */}
                                          <div style={{ marginBottom: '12px' }}>
                                            <strong>Control Measures:</strong>
                                            {formatControlMeasures(hazard.control_measures)}
                                          </div>

                                          {/* PPE Section */}
                                          <div style={{ marginBottom: '16px' }}>
                                            <strong>Personal Protective Equipment (PPE):</strong>
                                            {isPPELoading ? (
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                                <ProgressSpinner />
                                                <span style={{ fontSize: '0.85rem', color: '#666' }}>Loading PPE...</span>
                                              </div>
                                            ) : hazardPPE.length > 0 ? (
                                              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {hazardPPE.map((ppe) => (
                                                  <Chip
                                                    key={ppe.id}
                                                    label={ppe.name}
                                                    className={ppe.required ? 'chip-danger' : 'chip-info'}
                                                    icon={ppe.required ? 'pi pi-exclamation-triangle' : undefined}
                                                  />
                                                ))}
                                              </div>
                                            ) : (
                                              <div style={{ fontStyle: 'italic', color: '#666', marginTop: '4px', fontSize: '0.9rem' }}>
                                                No PPE requirements specified
                                              </div>
                                            )}
                                          </div>

                                          {/* Risks */}
                                          {hazard.risks && hazard.risks.length > 0 ? (
                                            <div>
                                              <strong>Risks:</strong>
                                              {hazard.risks.map((risk) => (
                                                <div key={risk.id} style={{ 
                                                  marginTop: '8px', 
                                                  padding: '12px', 
                                                  backgroundColor: '#f8f9fa', 
                                                  borderRadius: '6px',
                                                  border: '1px solid #e9ecef'
                                                }}>
                                                  <div style={{ marginBottom: '8px' }}>
                                                    <strong style={{ color: '#495057' }}>{risk.name}</strong>
                                                    {risk.risk_id && (
                                                      <span style={{ fontSize: '0.8rem', color: '#6c757d', marginLeft: '8px' }}>
                                                        (ID: {risk.risk_id})
                                                      </span>
                                                    )}
                                                  </div>
                                                  
                                                  {risk.description && (
                                                    <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#666' }}>
                                                      {risk.description}
                                                    </div>
                                                  )}

                                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                      <strong>Initial:</strong>{' '}
                                                      <Chip
                                                        label={risk.rating_label}
                                                        className={`${getRiskChipClassName(risk.rating_label)} text-xs`}
                                                      />
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                      <strong>Residual:</strong>{' '}
                                                      <Chip
                                                        label={risk.after_rating_label}
                                                        className={`${getRiskChipClassName(risk.after_rating_label)} text-xs`}
                                                      />
                                                    </div>
                                                  </div>

                                                  {risk.responsible_name && (
                                                    <div style={{ fontSize: '0.85rem', marginBottom: '4px' }}>
                                                      <strong>Responsible:</strong> {risk.responsible_name}
                                                    </div>
                                                  )}

                                                  {risk.review_date && (
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                      <strong>Review Date:</strong> {risk.review_date}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div style={{ fontStyle: 'italic', color: '#666', fontSize: '0.9rem' }}>
                                              No specific risks identified for this hazard
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </Card>
                                  );
                                })
                              ) : (
                                <p style={{ fontStyle: 'italic', color: '#999', fontSize: '0.85rem', margin: '6px 0' }}>
                                  No hazards identified for this job step
                                </p>
                              )}
                            </div>
                          )}
                        </Card>
                      );
                    })
                  ) : (
                    // Table View
                    renderTableView(category.riskAssessments)
                  )}
                </>
              )}
            </Card>
          );
        })
      )}

      {/* Custom Sign-off Section */}
      {signoffSection}
    </div>
  );
}